import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { GenericData } from '@common/formEngine/generic/generic.types';
import type { IGeneralPractitioner } from '@common/interfaces';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { dependsOn } from '@common/formEngine/dependsOn';
import { patient_verzekeringskosten_akkoord_field } from '@common/formEngine/generic/genericFields';
import { getGeneralPractitionerSchema } from '@common/validation/customControls/generalPractitioner';
import { emailSchema } from '@common/validation/schemas';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import type { Patient } from '../utils';
import { Slechthorendheid, slechthorendheidOptions } from '../utils';

export enum RedenVerwijzing {
    anders = 'Anders',
    meerAsymmetrie = 'Asymmetrie die meer optreedt',
    minderAsymmetrie = 'Asymmetrie die minder optreedt',
    toenameGehoorverlies = 'Toename gehoorverlies',
    verschilLuchtEnBeengeleiding = 'Verschil in lucht- en beengeleiding',
    vroegtijdigeOuderdomsslechthorendheid = 'Vroegtijdige ouderdomsslechthorendheid',
}

const redenVerwijzingOptions: RedenVerwijzing[] = [
    RedenVerwijzing.vroegtijdigeOuderdomsslechthorendheid,
    RedenVerwijzing.toenameGehoorverlies,
    RedenVerwijzing.meerAsymmetrie,
    RedenVerwijzing.minderAsymmetrie,
    RedenVerwijzing.verschilLuchtEnBeengeleiding,
    RedenVerwijzing.anders,
];

export const relevanteVoorgeschiedenisLabels = {
    anders: 'Anders',
    duizeligheid: 'Duizeligheid',
    geen: 'Geen',
    hersenvliesontsteking: 'Hersenvliesontsteking',
    klap_op_het_hoofd: 'Klap op het hoofd',
    oorontsteking: 'Oorontsteking',
    ooroperatie: 'Ooroperatie',
    oorpijn: 'Oorpijn',
    oorsuizen: 'Oorsuizen',
    slechthorendheid_in_familie: 'Slechthorendheid in familie',
    veel_oorsmeer: 'Veel oorsmeer',
    werkverleden_met_lawaai: 'Werk(verleden) met lawaai',
};

const relevanteVoorgeschiedenisSchema = Koi.object({
    anders: Koi.boolean().allow(null),
    duizeligheid: Koi.boolean().allow(null),
    geen: Koi.boolean().allow(null),
    hersenvliesontsteking: Koi.boolean().allow(null),
    klap_op_het_hoofd: Koi.boolean().allow(null),
    oorontsteking: Koi.boolean().allow(null),
    ooroperatie: Koi.boolean().allow(null),
    oorpijn: Koi.boolean().allow(null),
    oorsuizen: Koi.boolean().allow(null),
    slechthorendheid_in_familie: Koi.boolean().allow(null),
    veel_oorsmeer: Koi.boolean().allow(null),
    werkverleden_met_lawaai: Koi.boolean().allow(null),
});

export type HearingLossExaminationDataV1 = {
    assets: ProcessActivityAsset[];
    generalPractitioner: IGeneralPractitioner;
    motivatie_patient: string | null;
    overige_opmerkingen_toelichting: string | null;
    patient: Patient;
    reden_verwijzing: RedenVerwijzing | null;
    reden_verwijzing_anders: string | null;
    relevante_voorgeschiedenis: {
        anders: boolean;
        duizeligheid: boolean;
        geen: boolean;
        hersenvliesontsteking: boolean;
        klap_op_het_hoofd: boolean;
        oorontsteking: boolean;
        ooroperatie: boolean;
        oorpijn: boolean;
        oorsuizen: boolean;
        slechthorendheid_in_familie: boolean;
        veel_oorsmeer: boolean;
        werkverleden_met_lawaai: boolean;
    };
    relevante_voorgeschiedenis_toelichting: string | null;
    slechthorendheid: Slechthorendheid | null;
    toestemming_van_patient: boolean;
} & Pick<GenericData, 'patient_verzekeringskosten_akkoord'>;

export type HearingLossExaminationKeyV1 = DeepPickPath<HearingLossExaminationDataV1>;

export const hearingLossExaminationSectionsV1: Section<HearingLossExaminationDataV1>[] = [
    {
        fields: [
            patient_verzekeringskosten_akkoord_field,
            {
                draftSchema: Koi.boolean(),
                finalSchema: Koi.boolean().valid(true),
                initialValue: null,
                isRequired: true,
                key: 'toestemming_van_patient',
                label: 'Toestemming van patiënt',
                type: 'custom',
            },
        ],
    },
    {
        fields: [
            {
                draftSchema: getTextInputStringSchema(true, 255),
                finalSchema: getTextInputStringSchema(false, 255),
                isRequired: true,
                key: 'patient.mobileNumber',
                label: 'Telefoonnummer',
                type: 'textInput',
            },
            {
                draftSchema: getTextInputStringSchema(true, 255),
                finalSchema: emailSchema,
                isRequired: true,
                key: 'patient.emailAddress',
                label: 'E-mailadres',
                type: 'textInput',
            },
            {
                draftSchema: getGeneralPractitionerSchema(true),
                finalSchema: getGeneralPractitionerSchema(true),
                initialValue: {
                    organizationId: null,
                    practitionerId: null,
                },
                key: 'generalPractitioner',
                label: 'Huisarts van de patiënt',
                type: 'custom',
            },
        ],
        title: 'Gegevens patiënt',
    },
    {
        fields: [
            {
                draftSchema: Koi.koi().enum(RedenVerwijzing).allow(null),
                finalSchema: Koi.koi().enum(RedenVerwijzing),
                isRequired: true,
                key: 'reden_verwijzing',
                label: 'Reden van verwijzing',
                type: 'select',
                valueOptions: redenVerwijzingOptions,
            },
            {
                draftSchema: Koi.when('reden_verwijzing', {
                    is: RedenVerwijzing.anders,
                    otherwise: Koi.valid(null),
                    then: getTextAreaStringSchema().allow(null),
                }),
                finalSchema: Koi.when('reden_verwijzing', {
                    is: RedenVerwijzing.anders,
                    otherwise: Koi.valid(null),
                    then: getTextAreaStringSchema(),
                }),
                include: dependsOn(
                    'reden_verwijzing',
                    ({ reden_verwijzing }) => reden_verwijzing === RedenVerwijzing.anders
                ),
                isRequired: true,
                key: 'reden_verwijzing_anders',
                label: 'Toelichting anders',
                type: 'textArea',
            },
            {
                key: 'motivatie_patient',
                label: 'Motivatie van patiënt',
                type: 'textArea',
            },
            {
                draftSchema: Koi.koi().enum(Slechthorendheid).allow(null),
                finalSchema: Koi.koi().enum(Slechthorendheid),
                isRequired: true,
                key: 'slechthorendheid',
                label: 'Slechthorendheid',
                type: 'radioGroup',
                valueOptions: slechthorendheidOptions,
            },
            {
                draftSchema: relevanteVoorgeschiedenisSchema,
                finalSchema: relevanteVoorgeschiedenisSchema.anyFilledIn(),
                initialValue: {
                    anders: false,
                    duizeligheid: false,
                    geen: false,
                    hersenvliesontsteking: false,
                    klap_op_het_hoofd: false,
                    oorontsteking: false,
                    ooroperatie: false,
                    oorpijn: false,
                    oorsuizen: false,
                    slechthorendheid_in_familie: false,
                    veel_oorsmeer: false,
                    werkverleden_met_lawaai: false,
                },
                isRequired: true,
                key: 'relevante_voorgeschiedenis',
                label: 'Relevante voorgeschiedenis',
                type: 'custom',
            },
            {
                key: 'relevante_voorgeschiedenis_toelichting',
                label: 'Toelichting',
                type: 'textArea',
            },
        ],
        title: 'Anamnese',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'assets',
                label: 'Audiometrie & trommelvliesfoto',
                type: 'assets',
            },
        ],
        title: 'Onderzoeken',
    },
    {
        fields: [
            {
                key: 'overige_opmerkingen_toelichting',
                label: 'Toelichting',
                type: 'textArea',
            },
        ],
        title: 'Overige opmerkingen',
    },
];
